<script setup lang="ts">
import type {
  IMonitorCreditRatesResponse,
  IMonitorCreditRateItem
} from '~/components/widget/monitor'
import { EntityOrganizationLogo } from '~/components/entity/organization-logo'
import { useMonitorColumns, MONITOR_COLUMN_ANIMATION_DELAY_MS } from '../useMonitorColumns'

const props = defineProps<{
  type: string
}>()

const { t } = useI18n()

const selectedPeriod = ref<number | null>(null)

const requestParams = computed(() => ({
  type: props.type,
  ...(selectedPeriod.value ? { period: selectedPeriod.value } : {})
}))

const { data: response } = useApi<IMonitorCreditRatesResponse>('/monitor/credit-rates', {
  params: requestParams,
  key: computed(() => `monitor-credit-rates-${props.type}-${selectedPeriod.value ?? 'all'}`),
  watch: [requestParams]
})

// Cheapest first — the page sells credits, so the lowest rate leads.
const items = computed<IMonitorCreditRateItem[]>(() =>
  [...(response.value?.data ?? [])].sort((a, b) => a.rate - b.rate)
)

const meta = computed(() => response.value?.meta)

const visiblePeriods = computed(() => meta.value?.periods.filter((item) => item.is_visible) ?? [])

// Before a period is picked the API answers across all terms, and the first pill
// is what that response corresponds to. Deriving the active pill from the payload
// keeps server and client markup identical instead of flipping state after hydration.
const activePeriod = computed(() => selectedPeriod.value ?? visiblePeriods.value[0]?.period ?? null)

// The API hides a credit type when too few organizations report rates for it.
const isVisible = computed(() => meta.value?.is_visible !== false && items.value.length > 0)

// Credit rates sit in a wide band, so a low floor still keeps the cheapest bank readable.
const { barHeight, formatRate, logoSize } = useMonitorColumns({
  items,
  rate: (item) => item.rate,
  minBarPercent: 18
})
</script>

<template>
  <div v-if="isVisible" class="Card">
    <div class="mb-4 flex flex-col gap-1">
      <h2 class="text-lg font-semibold">
        {{ t('monitor.credit_rates.title') }}
      </h2>
      <p class="text-muted-foreground text-sm">
        {{ t('monitor.credit_rates.subtitle') }}
      </p>
    </div>

    <div v-if="visiblePeriods.length" class="mb-6 flex flex-wrap gap-2">
      <Button
        v-for="period in visiblePeriods"
        :key="period.period"
        :variant="activePeriod === period.period ? 'blue' : 'outline-gray'"
        size="sm"
        @click="selectedPeriod = period.period"
      >
        {{ t('monitor.credit_rates.period_months', { count: period.period }) }}
      </Button>
    </div>

    <Transition name="columns" mode="out-in">
      <ul
        :key="`${props.type}-${activePeriod ?? 'all'}`"
        class="flex items-end gap-2 overflow-x-auto pb-2 sm:gap-3 sm:overflow-x-visible"
      >
        <li
          v-for="(item, index) in items"
          :key="item.organization.id"
          class="MonitorColumn flex min-w-9 max-w-12 shrink-0 grow basis-10 flex-col items-center gap-2 sm:min-w-0 sm:shrink"
          :style="{ '--column-delay': `${index * MONITOR_COLUMN_ANIMATION_DELAY_MS}ms` }"
        >
          <div class="flex h-40 w-full flex-col justify-end gap-1">
            <span class="whitespace-nowrap text-center text-[11px] font-bold tabular-nums">
              {{ formatRate(item.rate) }}
            </span>
            <div
              class="MonitorBar w-full rounded-t-lg bg-blue-100"
              :style="{ '--bar-height': barHeight(item.rate) }"
              role="img"
              :aria-label="
                t('monitor.credit_rates.bar_label', {
                  org: item.organization.title,
                  rate: formatRate(item.rate)
                })
              "
            />
          </div>

          <EntityOrganizationLogo
            :src="item.organization.logo"
            :alt="item.organization.title"
            :to="item.organization.url"
            :aria-label="
              t('monitor.credit_rates.more_about_org', { org: item.organization.title })
            "
            :size="logoSize"
          />
        </li>
      </ul>
    </Transition>

    <div class="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 border-t pt-4 text-sm">
      <span>
        {{ t('monitor.credit_rates.market_avg') }}:
        <strong class="tabular-nums">{{ formatRate(meta?.market_avg) }}</strong>
      </span>
      <span>
        {{ t('monitor.credit_rates.market_range') }}:
        <strong class="tabular-nums">
          {{ formatRate(meta?.market_min) }} — {{ formatRate(meta?.market_max) }}
        </strong>
      </span>
      <span class="text-muted-foreground">
        {{ t('monitor.credit_rates.organizations', { count: meta?.organizations ?? 0 }) }}
      </span>
    </div>
  </div>
</template>

<style scoped>
/* Bar height comes from the data, so it arrives as a custom property per item. */
.MonitorBar {
  height: var(--bar-height);
}

.columns-enter-active,
.columns-leave-active {
  transition: opacity 0.25s ease;
}

.columns-enter-from,
.columns-leave-to {
  opacity: 0;
}

/* Columns rise one after another, so switching a period reads as a redraw. */
.MonitorColumn {
  animation: monitor-column-rise 0.4s ease-out backwards;
  animation-delay: var(--column-delay);
}

@keyframes monitor-column-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .MonitorColumn {
    animation: none;
  }

  .columns-enter-active,
  .columns-leave-active {
    transition: none;
  }
}
</style>
